import dayjs from "dayjs"
import http from "@services/httpService"
import type { PagedResultDto } from "@services/dto/pagedResultDto"
import type {
  GetNotificationAuditLogInput,
  NotificationAuditLogDto,
} from "@services/notification/dto/notificationAuditLogDto"
import { L } from "@lib/abpUtility"
import { downloadFile } from "@lib/helperFile"

/**
 * Client REST cho Notification Audit Log, bọc `httpService` (axios) theo URL ABP
 * `/api/services/app/NotificationAuditLog/{Method}` và envelope `{ result, success }`.
 *
 * Audit log là append-only: chỉ có `GetAll` (read) + `Export`.
 * `ExportAsync` backend trả về danh sách JSON rows (không phân trang, cap 10.000) — FE tự
 * format CSV để tải về. Tham chiếu: Requirement 12.3.
 */
class NotificationAuditLogService {
  private readonly baseUrl = "api/services/app/NotificationAuditLog"

  /** Cột export CSV theo đúng thứ tự — key `NotificationAuditLogDto` + localization key tiêu đề. */
  private readonly exportColumns: ReadonlyArray<{
    key: keyof NotificationAuditLogDto
    label: string
  }> = [
    { key: "creationTime", label: "AUDIT_LOG_CREATION_TIME" },
    { key: "bookingCode", label: "AUDIT_LOG_BOOKING_CODE" },
    { key: "notificationTypeName", label: "AUDIT_LOG_NOTIFICATION_TYPE" },
    { key: "notificationCode", label: "AUDIT_LOG_NOTIFICATION_CODE" },
    { key: "requestTypeName", label: "AUDIT_LOG_REQUEST_TYPE" },
    { key: "recipientName", label: "AUDIT_LOG_RECIPIENT_NAME" },
    { key: "recipientEmail", label: "AUDIT_LOG_RECIPIENT_EMAIL" },
    { key: "recipientPhone", label: "AUDIT_LOG_RECIPIENT_PHONE" },
    { key: "channelName", label: "AUDIT_LOG_CHANNEL" },
    { key: "templateName", label: "AUDIT_LOG_TEMPLATE" },
    { key: "statusName", label: "AUDIT_LOG_STATUS" },
    { key: "amount", label: "AUDIT_LOG_AMOUNT" },
    { key: "createdByName", label: "AUDIT_LOG_CREATED_BY" },
    { key: "errorMessage", label: "AUDIT_LOG_ERROR_MESSAGE" },
  ]

  /** Danh sách audit log có phân trang + filter, sort mặc định CreationTime DESC (R12.3). */
  public async getAll(
    input: GetNotificationAuditLogInput
  ): Promise<PagedResultDto<NotificationAuditLogDto>> {
    const res = await http.get(`${this.baseUrl}/GetAll`, { params: input })
    return res.data.result
  }

  /**
   * Export audit log ra CSV: fetch toàn bộ rows (backend cap 10.000) rồi tạo file client-side.
   * Dùng UTF-8 BOM để Excel hiển thị đúng tiếng Việt (R12.3).
   */
  public async export(input: GetNotificationAuditLogInput): Promise<void> {
    const res = await http.get(`${this.baseUrl}/Export`, { params: input })
    const rows: NotificationAuditLogDto[] = res.data.result ?? []
    const csv = this.buildCsv(rows)
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" })
    downloadFile(
      blob,
      `EXPORT-NOTIFICATION-AUDIT-LOG-${dayjs().format("DDMMYYYYHHmmss")}.csv`
    )
  }

  /** Dựng nội dung CSV (header + rows) từ danh sách audit log theo cấu hình cột. */
  private buildCsv(rows: NotificationAuditLogDto[]): string {
    const header = this.exportColumns
      .map((col) => this.escapeCsv(L(col.label)))
      .join(",")
    const body = rows.map((row) =>
      this.exportColumns
        .map((col) => this.escapeCsv(this.formatCell(row, col.key)))
        .join(",")
    )
    return [header, ...body].join("\r\n")
  }

  /** Chuẩn hoá giá trị một ô trước khi ghi CSV (format ngày, số tiền, null → rỗng). */
  private formatCell(
    row: NotificationAuditLogDto,
    key: keyof NotificationAuditLogDto
  ): string {
    const value = row[key]
    if (value === null || value === undefined) return ""
    if (key === "creationTime") {
      return dayjs(value as string).format("DD/MM/YYYY HH:mm:ss")
    }
    if (key === "amount") {
      return (value as number).toLocaleString("vi-VN")
    }
    return String(value)
  }

  /** Escape một trường CSV: bọc dấu ngoặc kép khi chứa `,` `"` hoặc xuống dòng. */
  private escapeCsv(value: string): string {
    if (/[",\r\n]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`
    }
    return value
  }
}

export default new NotificationAuditLogService()
